import { marcosDoMes, marcosDoRanking } from './marcos';
import { useCallback, useEffect, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { FotoOuIniciais } from '../../design/FotoDePerfil';
import { reais } from '../../premiacao';
import { useValoresOcultos } from '../../design/sigilo';
import { lerConquistas, lerMinhaMargem, meuPainel } from '../../vendas';
import type {
  Conquistas,
  IndicadorDoMeuPainel,
  IndicadorDaFaixa,
  JanelaDoDesempenho,
  MeuPainel,
  MinhaMargem,
  PosicaoNoRanking,
} from '../../vendas';
import { CartaoDaMetaDaEquipe } from './MetaDaEquipe';
import { BataASuaMarca, MetasDoMes } from './MinhaEvolucao';
import { MeusInsights } from './MeusInsights';
import { MeuBonusEMetas } from './MeuBonusEMetas';
import { AnaliseDetalhada } from './AnaliseDetalhada';
import { CartaoDeFaltas } from '../encomendas/MinhasFaltas';
import './desempenho.css';

/**
 * MEU PAINEL — o dashboard do balconista (dono, 25/09/2026).
 *
 * - As MÉDIAS da própria pessoa (tickets, itens e produtos por cupom, entregas), cada uma com a
 *   posição dela na equipe. O total vendido no dia ou no mês NÃO aparece (o Carlos não quer).
 * - Nos rankings, só a POSIÇÃO ("3º de 15"): ninguém vê quanto vendeu nem o número de colega.
 * - Quem está em 1º ganha o "Parabéns". O corte é do servidor; a tela só desenha.
 */

const JANELAS: readonly { j: JanelaDoDesempenho; rotulo: string }[] = [
  { j: 'hoje', rotulo: 'Hoje' },
  { j: 'semana', rotulo: 'Semana' },
  { j: 'mes', rotulo: 'Mês' },
];

const numero = (v: string, casas = 1) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

export function TelaDesempenho({ eu, sessao }: { eu: Eu | null; sessao: Sessao }) {
  const ocultos = useValoresOcultos();
  const [janela, setJanela] = useState<JanelaDoDesempenho>('hoje');
  const [d, setD] = useState<MeuPainel | null>(null);
  const [m, setM] = useState<MinhaMargem | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(() => {
    setErro(false);
    void meuPainel(janela).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
    // margem e desconto não derrubam a tela: sem eles, os cartões dizem "sem dado"
    setM(null);
    void lerMinhaMargem(janela).then((r) => {
      if (r.ok) setM(r.dados);
    });
  }, [janela]);
  useEffect(carregar, [carregar]);
  // metas do mês batidas (tela cheia) e subida no ranking (aviso): comemora quando acontece
  useEffect(() => {
    if (!d || !eu) return;
    marcosDoMes(eu.login, d);
    marcosDoRanking(eu.login, d);
  }, [d, eu]);

  const nome = eu ? eu.nomeExibicao || eu.nome : '';
  const mes = new Date().toLocaleDateString('pt-BR', {
    month: 'long',
    timeZone: 'America/Sao_Paulo',
  });
  const periodo = janela === 'hoje' ? 'hoje' : janela === 'semana' ? 'esta semana' : mes;
  const ate = d
    ? new Date(d.atualizadoEm).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      })
    : '—';
  const noPeriodo = janela === 'hoje' ? 'HOJE' : janela === 'semana' ? 'NA SEMANA' : 'NO MÊS';
  const t = d?.tickets;

  const seletor = (
    <div className="dp-janelas" role="tablist" aria-label="Período">
      {JANELAS.map((x) => (
        <button
          key={x.j}
          type="button"
          role="tab"
          aria-selected={janela === x.j}
          className={janela === x.j ? 'dp-sel' : ''}
          onClick={() => setJanela(x.j)}
        >
          {x.rotulo}
        </button>
      ))}
    </div>
  );

  return (
    <div className="dp">
      <header className="dp-movel-topo">
        <div className="dp-movel-linha">
          <img src="/img/inovapay-logo.png" alt="InovaPay" />
          <a className="dp-avatar" href="#/conta" aria-label="Minha conta">
            {nome ? <FotoOuIniciais foto={eu?.foto} nome={nome} /> : ''}
          </a>
        </div>
        <h1>Meu desempenho</h1>
        <div className="dp-movel-sub">
          {nome} · {periodo} · dados até {ate}
        </div>
      </header>

      <div className="dp-corpo">
        <div className="dp-titulo-pc">
          <div>
            <h1>Meu desempenho</h1>
            <div className="dp-sub">dados de vendas até {ate}</div>
          </div>
        </div>

        {/* TOPO (fase 3): o que estou ganhando e as minhas metas — sempre do mês. */}
        <MeuBonusEMetas sessao={sessao} />

        {/* BAIXO: como estou performando — o período vale só para esta parte. */}
        <div className="dp-detalhado">
          <div>
            <h2 className="dp-detalhado-titulo">Meu desempenho detalhado</h2>
            <div className="dp-sub">
              Entenda seus números e sua evolução ·{' '}
              {periodo.charAt(0).toUpperCase() + periodo.slice(1)}
            </div>
          </div>
          <div className="dp-detalhado-seletor">{seletor}</div>
        </div>

        {erro ? (
          <ErroDeArea oQue="o seu desempenho" aoTentar={carregar} />
        ) : (
          <>
            <MargemEDesconto m={m} periodo={periodo} />

            <h2 className="dp-secao">Minhas médias no HOS · {periodo}</h2>
            <div className="dp-hos" aria-busy={d === null} data-tour="ticket">
              <Cartao titulo="TICKET MÉDIO GERAL" sub="por cupom" i={t?.geral} formato="reais" />
              <Cartao titulo="ITENS POR CUPOM" sub="unidades" i={t?.itensPorCupom} />
              <Cartao
                titulo="PRODUTOS POR CUPOM"
                sub="produtos diferentes"
                i={t?.produtosPorCupom}
              />
              <Cartao titulo="TICKET DE ENTREGA" sub="por entrega" i={t?.entrega} formato="reais" />
              <div className="dp-cartao dp-hos-cartao">
                <div className="dp-hos-titulo">ENTREGAS POR DIA</div>
                <div className="dp-hos-pos">
                  {d?.entregas.porDia != null ? numero(String(d.entregas.porDia)) : '—'}
                </div>
                <div className="dp-hos-sub">
                  {d?.entregas.porDia != null
                    ? `média por dia trabalhado · ${periodo}`
                    : `sem entrega sua · ${periodo}`}
                </div>
              </div>
            </div>

            <div className="dp-atalhos">
              <a className="dp-atalho" href="#/minhas" data-tour="minhas-vendas">
                <b>Minhas vendas</b>
                <span>tudo que você lançou, por programa</span>
              </a>
              <a className="dp-atalho" href="#/premiacao" data-tour="minha-premiacao">
                <b>Minha premiação</b>
                <span>
                  {ocultos ? 'sua meta e seus itens da semana' : 'o que você vai receber na semana'}
                </span>
              </a>
            </div>

            {d && d.parabens.length > 0 && (
              <section className="dp-parabens" aria-label="Parabéns" role="status">
                <div className="dp-parabens-titulo">Parabéns, {primeiro(nome)}!</div>
                <ul>
                  {d.parabens.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="dp-posicoes-todas" aria-label={`Sua posição ${noPeriodo}`}>
              <div className="dp-posicoes-rotulo">SUA POSIÇÃO {noPeriodo}</div>
              <div className="dp-posicoes-grade">
                <Posicao r={d?.rankings.vendas} rotulo="Vendas" />
                <Posicao r={d?.rankings.vitamina} rotulo="Vitaminas" />
                <Posicao r={d?.rankings.preVencido} rotulo="Pré-vencidos" />
                <Posicao r={d?.rankings.paradao} rotulo="Paradão" />
                <Posicao r={d?.rankings.entregas} rotulo="Entregas" />
              </div>
            </section>

            <CartaoDaMetaDaEquipe />

            {d?.evolucao && <BataASuaMarca e={d.evolucao} />}
            {d?.metas && <MetasDoMes metas={d.metas} />}

            {!ocultos && (
              <>
                <h2 className="dp-secao">Minhas médias no InovaPay · {periodo}</h2>
                <div className="dp-hos" aria-busy={d === null}>
                  <Cartao
                    titulo="TICKET POR VENDA"
                    sub="os três programas"
                    i={t?.porVenda}
                    formato="reais"
                  />
                  <Cartao
                    titulo="TICKET VITAMINA"
                    sub="por venda"
                    i={t?.vitamina}
                    formato="reais"
                  />
                  <Cartao
                    titulo="TICKET PRÉ-VENCIDO"
                    sub="por venda"
                    i={t?.preVencido}
                    formato="reais"
                  />
                  <Cartao titulo="TICKET PARADÃO" sub="por venda" i={t?.paradao} formato="reais" />
                </div>
              </>
            )}

            <a className="dp-link-ranking dp-link-destaque" href="#/pagando">
              Ver o que está pagando
            </a>

            <h2 className="dp-secao">Minha evolução</h2>
            <AnaliseDetalhada />

            <MeusInsights />

            <CartaoDeConquistas />

            {podeFazer(eu, 'encomendas.registrar_falta') && <CartaoDeFaltas />}

            <a className="dp-link-ranking" href="#/como-funciona">
              Como funciona a premiação
            </a>

            {/* Bloco 11 (tela 24 C): os PRÓPRIOS casos do Giro auditados — nunca os dos colegas. */}
            <a className="dp-link-ranking" href="#/minhas-auditorias">
              Minhas auditorias do Giro
            </a>
            <p className="dp-nota">
              Aqui aparecem só as suas médias e a sua posição entre os colegas — ninguém vê quanto
              você vendeu, e você não vê o número de ninguém.
              {ocultos
                ? ' Entrega = cupom de tele-entrega no HOS. Empate divide a posição.'
                : ' Ticket do HOS = valor médio por cupom seu; ticket do InovaPay = valor médio de cada venda lançada; entrega = cupom de tele-entrega no HOS. Empate divide a posição.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dicaDaMargem = (i: IndicadorDaFaixa) =>
  i.proxima ? `suba ${pct(i.falta ?? 0)} pra ficar ${i.proxima}` : 'no verde, continue assim';
const dicaDoDesconto = (i: IndicadorDaFaixa) =>
  i.proxima ? `baixe ${pct(i.falta ?? 0)} pra ficar ${i.proxima}` : 'no verde, continue assim';

/**
 * O TOPO (dono, 28/09): margem e desconto médio com a COR da faixa e o que falta para a próxima,
 * mais cupons e ticket. Só os números da pessoa. Margem = lucro ÷ vendido dos itens com custo.
 */
function MargemEDesconto({ m, periodo }: { m: MinhaMargem | null; periodo: string }) {
  const ocultos = useValoresOcultos();
  return (
    <section className="dp-margem" aria-label="Minha margem e meu desconto" aria-busy={m === null}>
      <h2 className="dp-secao">Minha margem e meu desconto · {periodo}</h2>
      <div className="dp-margem-grade">
        <CartaoDeFaixa titulo="MARGEM" i={m?.margem} dica={dicaDaMargem} />
        <CartaoDeFaixa
          titulo="DESCONTO MÉDIO"
          sub="sem genérico e similar"
          i={m?.desconto}
          dica={dicaDoDesconto}
        />
        <div className="dp-cartao dp-hos-cartao">
          <div className="dp-hos-titulo">CUPONS</div>
          <div className="dp-hos-pos">{m ? m.cupons.toLocaleString('pt-BR') : '—'}</div>
          <div className="dp-hos-sub">{periodo}</div>
        </div>
        {!ocultos && (
          <div className="dp-cartao dp-hos-cartao">
            <div className="dp-hos-titulo">TICKET MÉDIO</div>
            <div className="dp-hos-pos">{m?.ticketMedio ? reais(Number(m.ticketMedio)) : '—'}</div>
            <div className="dp-hos-sub">por cupom · {periodo}</div>
          </div>
        )}
      </div>
      {m && (
        <p className="dp-nota">
          Margem: vermelho abaixo de {m.faixas.margem.amarelo}%, amarelo até {m.faixas.margem.azul}
          %, azul até {m.faixas.margem.verde}%, verde a partir de {m.faixas.margem.verde}%. Desconto
          (sem genérico e similar): verde até {m.faixas.desconto.amarelo}%, amarelo até{' '}
          {m.faixas.desconto.vermelho}%, vermelho acima. A margem conta tudo que tem custo do HOS
          {m.itens > 0 ? ` (${String(m.itensComCusto)} de ${String(m.itens)} itens)` : ''}.
        </p>
      )}
    </section>
  );
}

function CartaoDeFaixa({
  titulo,
  sub,
  i,
  dica,
}: {
  titulo: string;
  sub?: string;
  i: IndicadorDaFaixa | undefined;
  dica: (i: IndicadorDaFaixa) => string;
}) {
  const cor = i?.cor ?? null;
  return (
    <div className={`dp-cartao dp-hos-cartao dp-faixa${cor ? ` dp-faixa-${cor}` : ''}`}>
      <div className="dp-hos-titulo">
        {titulo}
        {sub ? <span className="dp-faixa-sub"> · {sub}</span> : null}
      </div>
      <div className="dp-hos-pos">{i && i.valor !== null ? pct(i.valor) : '—'}</div>
      <div className="dp-hos-sub">
        {i && i.valor !== null ? dica(i) : 'sem venda com esse dado'}
      </div>
    </div>
  );
}

function primeiro(nome: string) {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

function Cartao({
  titulo,
  sub,
  i,
  formato = 'numero',
}: {
  titulo: string;
  sub: string;
  i: IndicadorDoMeuPainel | undefined;
  formato?: 'reais' | 'numero';
}) {
  const ocultos = useValoresOcultos();
  // ticket em R$ é quanto ele vendeu: some o cartão inteiro (sem "—" no lugar)
  if (ocultos && formato === 'reais') return null;
  return (
    <div className="dp-cartao dp-hos-cartao">
      <div className="dp-hos-titulo">{titulo}</div>
      {i === undefined ? (
        <div className="dp-hos-pos">—</div>
      ) : i.disponivel ? (
        <>
          <div className="dp-hos-pos">
            {formato === 'reais' ? reais(Number(i.valor)) : numero(i.valor)}
          </div>
          <div className="dp-hos-sub">
            {sub} ·{' '}
            <b className={i.posicao === 1 ? 'dp-ouro-escuro' : undefined}>
              {i.posicao}º de {i.de}
            </b>
          </div>
        </>
      ) : (
        <>
          <div className="dp-hos-pos dp-indisponivel">—</div>
          <div className="dp-hos-sub">{i.porque}</div>
        </>
      )}
    </div>
  );
}

function Posicao({ r, rotulo }: { r: PosicaoNoRanking | undefined; rotulo: string }) {
  const pos = r?.posicao ?? null;
  return (
    <div className="dp-posicao">
      <div className={`dp-posicao-num${pos === 1 ? ' dp-ouro' : ''}`}>
        {pos ? `${String(pos)}º` : '—'}
      </div>
      <div className="dp-posicao-rotulo">
        {rotulo}
        {r ? (pos ? ` · de ${String(r.de)}` : ' · sem venda') : ''}
      </div>
    </div>
  );
}

/** Nível, sequência e medalhas (os marcos do 1.0), das próprias vendas — nunca dos colegas. */
function CartaoDeConquistas() {
  const [c, setC] = useState<Conquistas | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerConquistas().then((r) => (r.ok ? setC(r.dados) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);
  if (erro) return <ErroDeArea oQue="suas conquistas" aoTentar={carregar} />;
  if (!c) return <section className="dp-cartao dp-conquistas" aria-busy="true" />;
  const ganhas = c.medalhas.filter((m) => m.ganha).length;
  return (
    <section className="dp-cartao dp-conquistas" aria-label="Conquistas">
      <div className="dp-conq-topo">
        <div>
          <div className="dp-conq-rotulo">NÍVEL</div>
          <div className="dp-conq-nivel">{c.nivel.nome}</div>
          <div className="dp-conq-sub">
            {c.itens} {c.itens === 1 ? 'item vendido' : 'itens vendidos'}
            {c.nivel.proximo
              ? ` · faltam ${String(c.nivel.proximo.faltam)} para ${c.nivel.proximo.nome}`
              : ' · nível máximo'}
          </div>
        </div>
        <div>
          <div className="dp-conq-rotulo">SEQUÊNCIA</div>
          <div className="dp-conq-nivel">
            {c.sequencia.atual} {c.sequencia.atual === 1 ? 'dia' : 'dias'}
          </div>
          <div className="dp-conq-sub">
            recorde {c.sequencia.maior} · {c.diasAtivos} dias com venda
          </div>
        </div>
      </div>
      <div className="dp-conq-rotulo">
        MEDALHAS · {ganhas} de {c.medalhas.length}
      </div>
      <ul className="dp-medalhas">
        {c.medalhas.map((m) => (
          <li
            key={m.codigo}
            className={m.ganha ? 'dp-medalha dp-medalha-ganha' : 'dp-medalha'}
            title={m.descricao}
          >
            <b>{m.nome}</b>
            <span>{m.ganha ? m.descricao : `Falta: ${m.descricao.toLowerCase()}`}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
